import { memo } from 'react';
import { StyleSheet } from 'react-native';

import Arrow from '@/assets/icons/arrow';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { PlayerAvatar } from '@/components/player-avatar/PlayerAvatar';
import { PlayerAvatarData } from '@/components/player-avatar/player-avatar-config';
import { common } from '@/styles/styles';
import { Outcome, Projection } from '@/types/api.generated';
import { formatPlayerName } from '@/utils/format-player-name';

import { checkIfRegularProjection, checkIfSpecialOrFreePickProjection } from '../utils/lineupsUtils';
import { ProjectionIcons, SelectedProjectionDescription } from './PlayerDetails';

export const PLAYER_IMAGE_SIZE = 40;

type PlayerRowSmallProps = {
    player: PlayerAvatarData;
    projection: Pick<Projection, 'type' | 'value' | 'label' | 'nonRegularValue' | 'nonRegularPercentage'>;
    outcome?: Outcome;
    opponentLabel?: string;
    hideCrossedOutValue?: boolean;
};

export const PlayerRowSmall = memo(
    ({ player, projection, outcome, opponentLabel, hideCrossedOutValue }: PlayerRowSmallProps) => {
        const arrowStyle = outcome ? (outcome === Outcome.More ? {} : common.rotate180) : {};

        const crossedOutValue = projection.value;
        const isSpecialOrFreeProjection = checkIfSpecialOrFreePickProjection(projection.type);
        const isRegularProjection = checkIfRegularProjection(projection.type);
        const shouldShowCrossedOutValue =
            !hideCrossedOutValue && isSpecialOrFreeProjection && !!crossedOutValue && crossedOutValue > 0;
        const selectedProjectionValue = isRegularProjection ? projection.value : projection.nonRegularValue;

        // design wanted smaller projection icons just for Social feed player rows
        // scaled the existing projection, and this margin is needed to align the scaled icon properly
        const projectionIconMargin = isRegularProjection ? 0 : -3;

        return (
            <Row gap={'s12'} alignItems={'center'}>
                <Box style={styles.imageContainer}>
                    <PlayerAvatar playerData={player} playerAvatarVariant={'playerRow'} />
                </Box>
                {/* flex: 1 is required when opponentLabel exists to establish a width boundary that enables text truncation */}
                <Box gap={'s2'} flex={opponentLabel ? 1 : undefined}>
                    <Row gap={'s3'}>
                        <Text variant={'titleMedium'} lineHeight={20} color={ds.colorToken.text.primary}>
                            {formatPlayerName(player)}
                        </Text>
                        {opponentLabel ? (
                            <Text
                                variant={'titleMedium'}
                                color={ds.colorToken.text.secondary}
                                lineHeight={20}
                                fontWeight={'400'}
                                numberOfLines={1}
                                ellipsizeMode={'tail'}
                                style={styles.playerOpponentLabel}
                            >
                                {opponentLabel}
                            </Text>
                        ) : null}
                    </Row>
                    <Row>
                        <Row style={[styles.projectionIconContainer, { marginHorizontal: projectionIconMargin }]}>
                            <ProjectionIcons
                                selectedProjection={{
                                    type: projection.type,
                                    nonRegularPercentage: projection.nonRegularPercentage,
                                }}
                            />
                        </Row>
                        <Row gap={'s4'} alignItems={'center'}>
                            <Arrow color={ds.colorValue.icon.secondary} style={arrowStyle} />
                            <SelectedProjectionDescription
                                crossedOutValue={crossedOutValue}
                                projectionValue={selectedProjectionValue ?? 0}
                                shouldShowCrossedOutValue={shouldShowCrossedOutValue}
                                projectionDescription={projection.label}
                                color={ds.colorToken.text.secondary}
                            />
                        </Row>
                    </Row>
                </Box>
            </Row>
        );
    }
);

const styles = StyleSheet.create({
    imageContainer: {
        width: PLAYER_IMAGE_SIZE,
        height: PLAYER_IMAGE_SIZE,
        backgroundColor: ds.colorValue.background.neutral.subtle,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: ds.colorValue.border.subtle,
        overflow: 'hidden',
        alignItems: 'center',
    },
    projectionIconContainer: {
        transform: [{ scale: 0.8 }],
    },
    playerOpponentLabel: {
        flex: 1,
    },
});
